/**
 * AGC: Tournaments List Component
 * AGC: Displays all tournaments (ongoing, open, closed) with history
 */

import React, { useState, useEffect, useCallback } from 'react'
import { Trophy, Users, Clock, Award, ChevronDown, ChevronUp } from 'lucide-react'
import { useWebSocket } from '../../hooks/useWebSocket'
import { useAccount } from 'wagmi'
import { TournamentLobby } from './TournamentLobby'

interface Tournament {
  id: string
  status: 'WAITING_FOR_PLAYERS' | 'COUNTDOWN' | 'RUNNING' | 'FINISHED'
  minPlayersToStart: number
  maxPlayers: number
  createdAt: number
  autoStartAt?: number
  countdownEndsAt?: number
  currentRound?: number
  participantCount: number
  readyCount: number
  rewards: {
    first: { ogb: number; potions: number; nftItem?: string }
    second: { ogb: number; potions: number }
    third: { ogb: number; potions: number }
  }
  finalStandings?: Array<{ address: string; wins: number; losses: number; draws: number }>
  rewardsTxHash?: string
  rewardsFinalizedAt?: number
}

interface TournamentsListProps {
  onNavigateToPage?: (page: 'p2pBattle' | 'home' | 'games' | 'tournamentMatch') => void
  onSelectTournament?: (tournamentId: string) => void
  selectedTournamentId?: string | null
}

export const TournamentsList: React.FC<TournamentsListProps> = ({
  onNavigateToPage,
  onSelectTournament,
  selectedTournamentId
}) => {
  const [tournaments, setTournaments] = useState<Tournament[]>([])
  const [expandedHistory, setExpandedHistory] = useState(false)
  const { socket } = useWebSocket()
  const { address } = useAccount()

  const shortAddress = (addr: string) => {
    if (!addr || addr.length < 10) return addr || 'Unknown'
    return `${addr.slice(0, 6)}…${addr.slice(-4)}`
  }

  const formatTime = (ms: number) => {
    if (!Number.isFinite(ms) || ms <= 0) return '0:00'
    const minutes = Math.floor(ms / 60000)
    const seconds = Math.floor((ms % 60000) / 1000)
    return `${minutes}:${seconds.toString().padStart(2, '0')}`
  }

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp)
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit'
    })
  }
  
  // AGC: Format reward - only show OGB tokens (not potions/NFTs)
  const formatReward = (reward: { ogb: number; potions: number; nftItem?: string } | undefined) => {
    if (!reward) return 'N/A'
    // AGC: Only show OGB tokens
    if (reward.ogb > 0) return `${reward.ogb} OGB`
    return 'N/A'
  }

  const loadTournaments = useCallback(() => {
    if (socket) {
      socket.emit('get_all_tournaments')
    }
  }, [socket])

  useEffect(() => {
    loadTournaments()
    
    if (socket) {
      const handleAllTournaments = (data: Tournament[]) => {
        // AGC: Sort tournaments: RUNNING > COUNTDOWN > WAITING_FOR_PLAYERS > FINISHED
        const sorted = data.sort((a, b) => {
          const statusOrder = { RUNNING: 0, COUNTDOWN: 1, WAITING_FOR_PLAYERS: 2, FINISHED: 3 }
          const orderA = statusOrder[a.status] ?? 4
          const orderB = statusOrder[b.status] ?? 4
          if (orderA !== orderB) return orderA - orderB
          return b.createdAt - a.createdAt // Newer first within same status
        })
        setTournaments(sorted)
      }

      socket.on('all_tournaments', handleAllTournaments)
      socket.on('tournament_created', loadTournaments)
      socket.on('tournament_updated', loadTournaments)
      socket.on('tournament_finished', loadTournaments)

      return () => {
        socket.off('all_tournaments', handleAllTournaments)
        socket.off('tournament_created', loadTournaments)
        socket.off('tournament_updated', loadTournaments)
        socket.off('tournament_finished', loadTournaments)
      }
    }
  }, [socket, loadTournaments])

  // AGC: Refresh tournaments every 5 seconds
  useEffect(() => {
    const interval = setInterval(loadTournaments, 5000)
    return () => clearInterval(interval)
  }, [loadTournaments])

  const ongoingTournaments = tournaments.filter(t => t.status === 'RUNNING' || t.status === 'COUNTDOWN')
  const openTournaments = tournaments.filter(t => t.status === 'WAITING_FOR_PLAYERS')
  // AGC: Only show tournaments that were actually played on-chain (have rewards TX or final standings)
  const finishedTournaments = tournaments.filter(t => 
    t.status === 'FINISHED' && (t.rewardsTxHash || (t.finalStandings && t.finalStandings.length > 0))
  ).slice(0, expandedHistory ? 10 : 1)
  
  // AGC: State for tournament detail modal
  const [selectedTournamentDetail, setSelectedTournamentDetail] = useState<Tournament | null>(null)

  // AGC: If a tournament is selected, show its lobby
  if (selectedTournamentId) {
    const selectedTournament = tournaments.find(t => t.id === selectedTournamentId)
    if (selectedTournament) {
      return (
        <div>
          <button
            onClick={() => onSelectTournament?.(null)}
            className="mb-4 text-sm text-slate-400 hover:text-white flex items-center gap-2"
          >
            ← Back to Tournaments List
          </button>
          <TournamentLobby onNavigateToPage={onNavigateToPage} tournamentId={selectedTournamentId} />
        </div>
      )
    }
  }

  return (
    <div className="w-full space-y-4">
      {/* AGC: Ongoing Tournaments */}
      {ongoingTournaments.length > 0 && (
        <section>
          <h3 className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
            <Trophy className="w-4 h-4 text-yellow-400" />
            Ongoing Tournaments
          </h3>
          <div className="space-y-2">
            {ongoingTournaments.map((tournament) => (
              <button
                key={tournament.id}
                onClick={() => onSelectTournament?.(tournament.id)}
                className="w-full text-left p-3 bg-gradient-to-r from-orange-600/20 to-red-600/20 border border-orange-500/30 rounded-lg hover:border-orange-400/50 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs font-semibold text-white mb-1">
                      {tournament.status === 'RUNNING' ? '🔥 In Progress' : '⏳ Countdown'}
                    </div>
                    <div className="text-[10px] text-slate-400">
                      {tournament.participantCount}/{tournament.maxPlayers} players
                      {tournament.status === 'RUNNING' && tournament.currentRound && ` • Round ${tournament.currentRound}`}
                    </div>
                  </div>
                  <Trophy className="w-5 h-5 text-yellow-400" />
                </div>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* AGC: Open Tournaments */}
      {openTournaments.length > 0 && (
        <section>
          <h3 className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
            <Users className="w-4 h-4 text-emerald-400" />
            Open Tournaments
          </h3>
          <div className="space-y-2">
            {openTournaments.map((tournament) => (
              <button
                key={tournament.id}
                onClick={() => onSelectTournament?.(tournament.id)}
                className="w-full text-left p-3 bg-gradient-to-r from-emerald-600/20 to-teal-600/20 border border-emerald-500/30 rounded-lg hover:border-emerald-400/50 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs font-semibold text-white mb-1">
                      Join Tournament
                    </div>
                    <div className="text-[10px] text-slate-400">
                      {tournament.participantCount}/{tournament.maxPlayers} players
                      {tournament.autoStartAt && (
                        <span> • Auto-starts in {formatTime(Math.max(0, tournament.autoStartAt - Date.now()))}</span>
                      )}
                    </div>
                  </div>
                  <Users className="w-5 h-5 text-emerald-400" />
                </div>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* AGC: Tournament History */}
      {finishedTournaments.length > 0 && (
        <section>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-white flex items-center gap-2">
              <Award className="w-4 h-4 text-purple-400" />
              Tournament History
            </h3>
            {tournaments.filter(t => 
              t.status === 'FINISHED' && (t.rewardsTxHash || (t.finalStandings && t.finalStandings.length > 0))
            ).length > 1 && (
              <button
                onClick={() => setExpandedHistory(!expandedHistory)}
                className="text-xs text-slate-400 hover:text-white flex items-center gap-1"
              >
                {expandedHistory ? (
                  <>
                    <ChevronUp className="w-3 h-3" />
                    Show Less
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-3 h-3" />
                    Show More ({Math.min(
                      tournaments.filter(t => 
                        t.status === 'FINISHED' && (t.rewardsTxHash || (t.finalStandings && t.finalStandings.length > 0))
                      ).length - 1, 
                      9
                    )} more)
                  </>
                )}
              </button>
            )}
          </div>
          <div className="space-y-2">
            {finishedTournaments.map((tournament) => (
              <button
                key={tournament.id}
                onClick={() => setSelectedTournamentDetail(tournament)}
                className="w-full text-left p-3 bg-slate-800/30 border border-slate-700/50 rounded-lg hover:border-slate-600/70 hover:bg-slate-800/50 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="text-xs font-semibold text-white">
                    {formatDate(tournament.rewardsFinalizedAt || tournament.createdAt)}
                  </div>
                  {tournament.rewardsTxHash && (
                    <a
                      href={`https://sepolia.basescan.org/tx/${tournament.rewardsTxHash}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-[10px] text-blue-400 hover:text-blue-300 underline font-mono"
                    >
                      {tournament.rewardsTxHash.slice(0, 8)}...{tournament.rewardsTxHash.slice(-6)}
                    </a>
                  )}
                </div>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* AGC: No Tournaments Message */}
      {tournaments.length === 0 && (
        <div className="text-center py-8">
          <Trophy className="w-12 h-12 text-slate-600 mx-auto mb-4" />
          <p className="text-slate-400 text-sm">No tournaments available</p>
          <p className="text-slate-500 text-xs mt-1">New tournaments are created automatically when players are online</p>
        </div>
      )}

      {/* AGC: Tournament Detail Modal */}
      {selectedTournamentDetail && (
        <>
          <div 
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50"
            onClick={() => setSelectedTournamentDetail(null)}
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div 
              className="w-full max-w-md bg-slate-900 border border-slate-700 rounded-2xl shadow-xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* AGC: Modal Header */}
              <div className="p-4 border-b border-slate-700 flex items-center justify-between">
                <h3 className="text-lg font-bold text-white">Tournament Results</h3>
                <button
                  onClick={() => setSelectedTournamentDetail(null)}
                  className="text-slate-400 hover:text-white text-xl"
                >
                  ×
                </button>
              </div>

              {/* AGC: Modal Content */}
              <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
                {/* AGC: Results Section */}
                {selectedTournamentDetail.finalStandings && Array.isArray(selectedTournamentDetail.finalStandings) && selectedTournamentDetail.finalStandings.length > 0 ? (
                  <div className="space-y-3">
                    <h4 className="text-sm font-semibold text-white border-b border-slate-700 pb-2">Results</h4>
                    {selectedTournamentDetail.finalStandings.slice(0, 3).map((standing, index) => {
                      if (!standing?.address) return null
                      
                      const position = index + 1
                      const reward = position === 1 
                        ? selectedTournamentDetail.rewards?.first
                        : position === 2
                        ? selectedTournamentDetail.rewards?.second
                        : selectedTournamentDetail.rewards?.third
                      
                      return (
                        <div
                          key={standing.address || index}
                          className="flex items-center justify-between p-2 bg-slate-800/50 border border-slate-700 rounded-lg"
                        >
                          <div className="flex items-center gap-2 flex-1">
                            {/* AGC: Medal icons - Gold 🥇, Silver 🥈, Bronze 🥉 */}
                            <span className="text-2xl">
                              {position === 1 ? '🥇' : position === 2 ? '🥈' : '🥉'}
                            </span>
                            <span className="text-white text-sm font-mono">
                              {shortAddress(standing.address)}
                              {standing.address?.toLowerCase() === address?.toLowerCase() && (
                                <span className="ml-1 text-purple-400">(You)</span>
                              )}
                            </span>
                            <span className="text-slate-300 text-sm">
                              {standing.wins || 0}W {standing.losses || 0}L {standing.draws > 0 ? `${standing.draws}D` : ''}
                            </span>
                            <span className="text-emerald-400 text-sm font-semibold ml-auto">
                              {formatReward(reward)}
                            </span>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                ) : (
                  <div className="text-center py-4 text-slate-400 text-sm">
                    No standings data available
                  </div>
                )}

                {/* AGC: Tournament Info */}
                <div className="space-y-2 pt-2 border-t border-slate-700">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm text-slate-400">Tournament ID:</span>
                    <div className="flex items-center gap-2 flex-1 justify-end">
                      <span className="text-white font-mono text-xs">
                        {selectedTournamentDetail.id.length > 20 
                          ? `${selectedTournamentDetail.id.slice(0, 10)}...${selectedTournamentDetail.id.slice(-8)}`
                          : selectedTournamentDetail.id}
                      </span>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(selectedTournamentDetail.id)
                          // AGC: Visual feedback could be added here
                        }}
                        className="text-slate-400 hover:text-white text-xs px-2 py-1 border border-slate-600 rounded hover:border-slate-500 transition-colors flex-shrink-0"
                        title="Copy Tournament ID"
                      >
                        Copy
                      </button>
                    </div>
                  </div>
                  {selectedTournamentDetail.rewardsTxHash && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-400">Tournament TX:</span>
                      <a
                        href={`https://sepolia.basescan.org/tx/${selectedTournamentDetail.rewardsTxHash}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-400 hover:text-blue-300 underline text-xs font-mono"
                      >
                        {selectedTournamentDetail.rewardsTxHash.slice(0, 10)}...{selectedTournamentDetail.rewardsTxHash.slice(-8)}
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

